/**
 * A simple in-memory cache service for the client application
 * Provides methods to store, retrieve, and invalidate cached data
 */

interface CacheItem<T> {
    data: T;
    timestamp: number;
    expiresAt: number;
}

class CacheService {
    private cache: Map<string, CacheItem<any>> = new Map();
    
    // Default expiration time is 5 minutes (in milliseconds)
    private readonly DEFAULT_EXPIRATION = 5 * 60 * 1000;
    
    /**
     * Set an item in the cache
     * @param key The cache key
     * @param data The data to cache
     * @param expirationMs Optional expiration time in milliseconds
     */
    set<T>(key: string, data: T, expirationMs?: number): void {
        const now = Date.now();
        const expiration = expirationMs || this.DEFAULT_EXPIRATION;
        
        this.cache.set(key, {
            data,
            timestamp: now,
            expiresAt: now + expiration
        });
        
        console.log(`Cache: Set item '${key}'`);
    }
    
    /**
     * Get an item from the cache
     * @param key The cache key
     * @returns The cached data or null if not found or expired
     */
    get<T>(key: string): T | null {
        const item = this.cache.get(key);
        
        if (!item) {
            console.log(`Cache: Miss for key '${key}'`);
            return null;
        }
        
        const now = Date.now();
        
        // Check if the item has expired
        if (now > item.expiresAt) {
            console.log(`Cache: Expired item '${key}'`);
            this.cache.delete(key);
            return null;
        }
        
        console.log(`Cache: Hit for key '${key}'`);
        return item.data as T;
    }
    
    /**
     * Check if an item exists in the cache and is not expired
     * @param key The cache key
     * @returns True if the item exists and is not expired
     */
    has(key: string): boolean {
        const item = this.cache.get(key);
        
        if (!item) {
            return false;
        }
        
        const now = Date.now();
        
        // Check if the item has expired
        if (now > item.expiresAt) {
            this.cache.delete(key);
            return false;
        }
        
        return true;
    }
    
    /**
     * Remove an item from the cache
     * @param key The cache key
     */
    remove(key: string): void {
        this.cache.delete(key);
        console.log(`Cache: Removed item '${key}'`);
    }
    
    /**
     * Remove all items from the cache that match a prefix
     * @param keyPrefix The prefix to match
     */
    removeByPrefix(keyPrefix: string): void {
        const keysToRemove: string[] = [];
        
        this.cache.forEach((_, key) => {
            if (key.startsWith(keyPrefix)) {
                keysToRemove.push(key);
            }
        });
        
        keysToRemove.forEach(key => this.cache.delete(key));
        console.log(`Cache: Removed ${keysToRemove.length} items with prefix '${keyPrefix}'`);
    }
    
    /**
     * Clear all items from the cache
     */
    clear(): void {
        this.cache.clear();
        console.log('Cache: Cleared all items');
    }
    
    /**
     * Get or set an item in the cache
     * If the item doesn't exist or has expired, the factory function is called to get the data
     * @param key The cache key
     * @param factory A function that returns the data to cache
     * @param expirationMs Optional expiration time in milliseconds
     * @returns The cached data or the result of the factory function
     */
    async getOrSet<T>(key: string, factory: () => Promise<T>, expirationMs?: number): Promise<T> {
        const cachedItem = this.get<T>(key);
        
        if (cachedItem !== null) {
            return cachedItem;
        }
        
        const data = await factory();
        this.set(key, data, expirationMs);
        return data;
    }
}

export const cacheService = new CacheService(); 